/**
 * SocialInsightPanel - tach tu MD-26 Social.tsx 2026-05-12.
 *
 * Reusable component hien KPI + brief + winners/losers + by_category + by_topic + recommendations
 * cho FB Page Asklaw. Dung 2 noi:
 *   - Embedded trong MD-29 Content tab Social (props.scope='embedded', an h1 redundant)
 *   - (Truoc): standalone /dashboard/social - DA REDIRECT, khong dung nua
 *
 * Spec: F-A-CONTENT-MGMT.md AC-17 + DECISIONS s2026-05-12-HUB-PRUNE-MD26-MERGE-MD29.
 */

import { useCallback, useState } from 'react';
import { useAdminSocial, postRefresh } from './use-admin-social';
import type { InsightResponse, BriefMeta } from '@core/social/types';

function fmtPercent(x: number): string {
  return `${(x * 100).toFixed(1)}%`;
}
function fmtNum(x: number): string {
  if (x >= 1000) return `${(x / 1000).toFixed(1)}k`;
  return String(Math.round(x));
}
function ageStr(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)} phut truoc`;
  if (hours < 24) return `${hours.toFixed(1)}h truoc`;
  return `${Math.round(hours / 24)} ngay truoc`;
}

function VerdictPill({ verdict }: { verdict: string }) {
  const map: Record<string, { label: string; bg: string; fg: string }> = {
    double_down: { label: 'DOUBLE DOWN', bg: '#dcfce7', fg: '#166534' },
    promote: { label: 'PROMOTE', bg: '#dbeafe', fg: '#1e3a8a' },
    maintain: { label: 'MAINTAIN', bg: '#f3f4f6', fg: '#374151' },
    test_more: { label: 'TEST MORE', bg: '#fef3c7', fg: '#92400e' },
    demote: { label: 'DEMOTE', bg: '#fee2e2', fg: '#991b1b' },
    keep_peak: { label: 'KEEP PEAK', bg: '#dcfce7', fg: '#166534' },
    keep: { label: 'KEEP', bg: '#f3f4f6', fg: '#374151' },
    test: { label: 'TEST', bg: '#fef3c7', fg: '#92400e' },
    drop: { label: 'DROP', bg: '#fee2e2', fg: '#991b1b' },
  };
  const m = map[verdict] ?? { label: verdict, bg: '#e5e7eb', fg: '#111' };
  return (
    <span
      style={{
        background: m.bg,
        color: m.fg,
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {m.label}
    </span>
  );
}

export interface SocialInsightPanelProps {
  /** 'embedded' an h1 va padding ngoai; 'standalone' bao gom h1 + max-width. */
  scope?: 'embedded' | 'standalone';
}

export function SocialInsightPanel({ scope = 'standalone' }: SocialInsightPanelProps) {
  const [windowSel, setWindowSel] = useState<'7d' | '30d'>('7d');
  const insight = useAdminSocial<InsightResponse>(`/insight?window=${windowSel}`);
  const brief = useAdminSocial<BriefMeta>(`/brief/latest`);
  const [refreshing, setRefreshing] = useState(false);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await postRefresh('all');
      await insight.refetch();
    } catch (_e) {
      /* swallow */
    } finally {
      setRefreshing(false);
    }
  }, [insight]);

  const wrapStyle: React.CSSProperties =
    scope === 'standalone'
      ? { maxWidth: 1100, margin: '0 auto', padding: '24px 16px', fontFamily: 'system-ui, sans-serif' }
      : { padding: '8px 0 0 0', fontFamily: 'system-ui, sans-serif' };

  if (insight.loading) return <div style={wrapStyle}>Dang tai insight...</div>;
  if (insight.error) return <div style={{ ...wrapStyle, color: 'crimson' }}>Loi: {insight.error}</div>;
  if (!insight.data) return <div style={wrapStyle}>Chua co du lieu</div>;

  const d = insight.data;

  return (
    <div className="social-insight-panel" style={wrapStyle}>
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        {scope === 'standalone' ? (
          <h1 style={{ fontSize: 24, margin: 0 }}>Social Insight (FB Page)</h1>
        ) : (
          <h2 style={{ fontSize: 18, margin: 0 }}>Phan tich Fanpage Asklaw</h2>
        )}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <select value={windowSel} onChange={(e) => setWindowSel(e.target.value as '7d' | '30d')}>
            <option value="7d">7 ngay qua</option>
            <option value="30d">30 ngay qua</option>
          </select>
          <span style={{ fontSize: 12, color: '#666' }}>Dong bo: {ageStr(d.synced_age_hours)}</span>
          <button onClick={onRefresh} disabled={refreshing}>
            {refreshing ? 'Dang lam moi...' : 'Lam moi ngay'}
          </button>
        </div>
      </header>

      <section style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 24, fontSize: 14 }}>
        <span>
          <b>{d.totals.post_count}</b> bai
        </span>
        <span>
          Tiep can <b>{fmtNum(d.totals.total_reach)}</b> (TB {fmtNum(d.totals.avg_reach)}/bai)
        </span>
        <span>
          Tuong tac <b>{fmtPercent(d.totals.avg_engagement_rate)}</b>
        </span>
        <span>
          Click link <b>{fmtNum(d.totals.total_link_clicks)}</b>
        </span>
        <span>
          Follower delta{' '}
          <b>
            {d.totals.follower_delta >= 0 ? '+' : ''}
            {d.totals.follower_delta}
          </b>
        </span>
      </section>

      {d.alerts.length > 0 && (
        <section style={{ background: '#fef3c7', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          {d.alerts.map((a, i) => (
            <div key={i} style={{ fontSize: 13 }}>
              Canh bao: {a.message}
            </div>
          ))}
        </section>
      )}

      <section style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16 }}>Brief gan nhat {brief.data ? `(${brief.data.week_label})` : ''}</h3>
        {brief.loading && <p>Dang tai brief...</p>}
        {brief.error && <p style={{ color: 'crimson' }}>Chua co brief: {brief.error}</p>}
        {brief.data && (
          <article
            style={{
              background: '#f9fafb',
              padding: 16,
              borderRadius: 6,
              whiteSpace: 'pre-wrap',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            {brief.data.content_md}
          </article>
        )}
      </section>

      <section style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16 }}>Top bai (rank desc)</h3>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
              <th style={{ padding: 8 }}>Bai</th>
              <th style={{ padding: 8 }}>Loai</th>
              <th style={{ padding: 8 }}>Tiep can</th>
              <th style={{ padding: 8 }}>Tuong tac</th>
              <th style={{ padding: 8 }}>CTR</th>
              <th style={{ padding: 8 }}></th>
            </tr>
          </thead>
          <tbody>
            {d.winners.map((p) => (
              <tr key={p.fb_post_id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: 8, maxWidth: 360 }}>{p.caption_snippet}...</td>
                <td style={{ padding: 8 }}>{p.content_category ?? '-'}</td>
                <td style={{ padding: 8 }}>{fmtNum(p.reach)}</td>
                <td style={{ padding: 8 }}>{fmtPercent(p.engagement_rate)}</td>
                <td style={{ padding: 8 }}>{p.ctr != null ? fmtPercent(p.ctr) : '-'}</td>
                <td style={{ padding: 8 }}>
                  <a href={p.fb_post_url} target="_blank" rel="noreferrer">
                    Mo FB
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {d.losers.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 16 }}>Bai yeu</h3>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <tbody>
              {d.losers.map((p) => (
                <tr key={p.fb_post_id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: 8, maxWidth: 360 }}>{p.caption_snippet}...</td>
                  <td style={{ padding: 8 }}>{p.content_category ?? '-'}</td>
                  <td style={{ padding: 8 }}>{fmtNum(p.reach)}</td>
                  <td style={{ padding: 8 }}>{fmtPercent(p.engagement_rate)}</td>
                  <td style={{ padding: 8 }}>
                    <a href={p.fb_post_url} target="_blank" rel="noreferrer">
                      Mo FB
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16 }}>Theo loai bai</h3>
        <table style={{ width: '100%', fontSize: 13 }}>
          <tbody>
            {d.by_category.map((c) => (
              <tr key={c.category}>
                <td style={{ padding: 6 }}>
                  <b>{c.category}</b>
                </td>
                <td style={{ padding: 6 }}>{c.post_count} bai</td>
                <td style={{ padding: 6 }}>tiep can {fmtNum(c.avg_reach)}</td>
                <td style={{ padding: 6 }}>tuong tac {fmtPercent(c.avg_engagement_rate)}</td>
                <td style={{ padding: 6 }}>
                  <VerdictPill verdict={c.verdict} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16 }}>Theo chu de</h3>
        <table style={{ width: '100%', fontSize: 13 }}>
          <tbody>
            {d.by_topic.map((t) => (
              <tr key={t.topic}>
                <td style={{ padding: 6 }}>
                  <b>{t.topic}</b>
                </td>
                <td style={{ padding: 6 }}>{t.post_count} bai</td>
                <td style={{ padding: 6 }}>tiep can {fmtNum(t.avg_reach)}</td>
                <td style={{ padding: 6 }}>tuong tac {fmtPercent(t.avg_engagement_rate)}</td>
                <td style={{ padding: 6 }}>
                  <VerdictPill verdict={t.verdict} />
                </td>
                <td style={{ padding: 6, fontSize: 11, color: '#666' }}>
                  {t.subtopics_top.slice(0, 3).join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16 }}>Khuyen nghi ({d.recommendations.length})</h3>
        {d.recommendations.map((r) => (
          <div
            key={r.id}
            style={{
              borderLeft: `4px solid ${
                r.priority === 'high' ? '#dc2626' : r.priority === 'medium' ? '#f59e0b' : '#9ca3af'
              }`,
              padding: '8px 12px',
              marginBottom: 8,
              background: '#f9fafb',
            }}
          >
            <div style={{ fontSize: 12, color: '#666' }}>
              <b>{r.priority.toUpperCase()}</b> | {r.type} | {r.owner}
            </div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              <b>Muc tieu:</b> {r.target}
            </div>
            <div style={{ fontSize: 13, marginTop: 4, color: '#374151' }}>{r.rationale}</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              <b>Hanh dong:</b> {r.action}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
